import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { DataEntity } from '@barthauer/abc/graphics/adapters/open-layers';
import {
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { Subject } from 'rxjs';
import {
  MtTicketCreation,
  MtTicketCreationDocument,
  MtTicketTaskCreation,
} from '../create-works-wizard/models/tickets.model';
import { DataChangedEventArgs, DataChangedType } from '@barthauer/abc/ui-components';
import { CreateTicketService } from './services/create-ticket.service';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { DxValidationGroupComponent } from 'devextreme-angular';

@Component({
  selector: 'basys-opera-wizards-create-ticket-wizard',
  templateUrl: './create-ticket-wizard.component.html',
  styleUrls: ['./create-ticket-wizard.component.scss'],
  standalone: false,
})
export class CreateTicketWizardComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper: MatStepper;
  @Output() closeWizardDialog = new EventEmitter();
  @Output() showTicket = new EventEmitter<{
    ticketId: string;
    ticketDesignation: string;
    entity: DataEntity;
    show: boolean;
  }>();

  private resAsync: Promise<ResourceSet>;
  protected res = ResourceSet.Empty;
  protected ticketCreationData: MtTicketCreation = new MtTicketCreation();
  private selectedDataEntity: DataEntity;
  private stepValGroups = new Map<number, DxValidationGroupComponent>();
  private unsubscribe$ = new Subject<void>();
  private currentUserResourceId: string;
  protected isGenerating: boolean = false;
  protected dringlichkeitListDataSource: any;
  protected kategorieListDatasource: any;
  protected personnelResourcesDataSource: any;
  protected assignResourceForPush: boolean = false;
  protected assignedResourceIdForPush: string;

  constructor(
    private createTicketService: CreateTicketService,
    private messageBoxService: MessageBoxService,
    private resourceService: ResourceManagerService,
  ) {
    this.resAsync = this.resourceService.getResourceSet('opera-wizards');
  }

  async ngOnInit(): Promise<void> {
    this.res = await this.resAsync;
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
  private onTicketGenerationFailed(err: any): void {
    let message = err.message;
    this.showMessageAsHtml(message, MessageMode.Error);
    this.isGenerating = false;
  }

  public async init(
    entities: DataEntity[],
    priorityListDataSource: any = undefined,
    categoryListDatasource: any = undefined,
    personnelResourcesDataSourceItems: any = undefined,
    currentUserResourceId: string,
  ): Promise<void> {
    this.res = await this.resAsync; //calles ngOnInit also
    this.selectedDataEntity = entities[0];
    this.dringlichkeitListDataSource = priorityListDataSource?.dataItems; //changed for offline mode
    this.kategorieListDatasource = categoryListDatasource?.dataItems;
    this.personnelResourcesDataSource = personnelResourcesDataSourceItems;
    this.currentUserResourceId = currentUserResourceId;

    this.ticketCreationData.Status = '0';
    this.ticketCreationData.Herkunft = '2';
  }

  private addTicketTask(
    bezeichnung: string,
    lfdNr: number,
    status: string,
    startDatum: Date,
    startZeit: Date,
    endDatum: Date,
    endZeit: Date,
    idRefMtResource: string,
    createActiveReminder: boolean,
  ): void {
    const newTask: MtTicketTaskCreation = new MtTicketTaskCreation();
    newTask.Bezeichnung = bezeichnung;
    newTask.LfdNr = lfdNr;
    newTask.Status = status;
    newTask.StartDatum = startDatum;
    newTask.StartZeit = startZeit;
    newTask.EndDatum = endDatum;
    newTask.EndZeit = endZeit;
    newTask.IdRefMtResource = idRefMtResource;
    newTask.CreateActiveReminder = createActiveReminder;
    this.ticketCreationData.Tasks.push(newTask);
  }

  public async generateTicket(): Promise<void> {
    try {
      this.isGenerating = true;

      const now = new Date();
      this.ticketCreationData.StartDatum = now;
      if (this.ticketCreationData.Dringlichkeit) {
        this.ticketCreationData.EndDatum = this.calcEndDateByPriority(now, this.ticketCreationData.Dringlichkeit);
      }
      this.addTicketTask(
        this.res.get('InitialTicketTask-Designation'),
        1,
        '2',
        now,
        now,
        now,
        now,
        this.currentUserResourceId,
        false,
      );

      if (this.assignResourceForPush) {
        this.addTicketTask(
          this.res.get('PushMessageTicketTask-Designation'),
          2,
          '2',
          now,
          now,
          now,
          now,
          this.assignedResourceIdForPush,
          true,
        );
      }

      await this.createTicketService.generateTicketForEntity(this.ticketCreationData, this.selectedDataEntity);

      this.showTicket.emit({
        ticketId: this.ticketCreationData.Id,
        ticketDesignation: this.ticketCreationData.Bezeichnung,
        entity: this.selectedDataEntity,
        show: true,
      });

      this.closeWizardDialog.emit();
    } catch (err) {
      this.onTicketGenerationFailed(err);
    } finally {
      this.isGenerating = false;
    }
  }

  private calcEndDateByPriority(startDate: Date, priority: string) {
    let endDate: Date = new Date(startDate);
    switch (priority) {
      case '3':
        endDate.setDate(endDate.getDate() + 5);
        break;
      case '4':
        endDate.setDate(endDate.getDate() + 28);
        break;
    }
    return endDate;
  }

  protected registerGroup(index: number, group: DxValidationGroupComponent) {
    this.stepValGroups.set(index, group);
  }

  protected stepSelectionChange(event: StepperSelectionEvent, stepper: MatStepper): void {
    if (this.stepValGroups.has(event.previouslySelectedIndex)) {
      const result = this.stepValGroups.get(event.previouslySelectedIndex).instance.validate();
      if (!result.isValid) {
        setTimeout(() => {
          stepper.selectedIndex = event.previouslySelectedIndex;
        }, 0);
      }
    }
  }

  private showMessageAsHtml(htmlMsg: string, messageMode: MessageMode = MessageMode.Info): void {
    const settings = new MessageSettings();
    settings.mode = messageMode;
    let icon = MessageBoxIcon.Info;
    switch (messageMode) {
      case MessageMode.Error:
        icon = MessageBoxIcon.Error;
        break;
      case MessageMode.Warning:
        icon = MessageBoxIcon.Warning;
        break;
      case MessageMode.Question:
        icon = MessageBoxIcon.Question;
        break;
    }
    settings.icon = icon;
    settings.button = MessageBoxButtons.OK;
    settings.htmlcode = document.createElement('div');
    settings.htmlcode.innerHTML = htmlMsg;

    this.messageBoxService.showMessageBox(settings).subscribe();
  }

  public mediaGalleryDataChanged(event: DataChangedEventArgs): void {
    switch (event.type) {
      case DataChangedType.Added:
        for (const document of event.documents) {
          const mtTicketDoc: MtTicketCreationDocument = new MtTicketCreationDocument();
          mtTicketDoc.Designation = document.designation;
          mtTicketDoc.DataUrl = document.previewImageDataUrl;
          mtTicketDoc.File = document.file;
          this.ticketCreationData.Documents.push(mtTicketDoc);
        }
        break;
    }
  }
}
